import React, { useEffect, useState, useRef } from 'react';
import { CheckCircleIcon, XIcon } from 'lucide-react';
// Move purchases data outside component to prevent re-creation on renders
const purchases = [{
  name: 'Emily',
  location: 'Austin',
  time: '2 minutes ago',
  price: '$14.97'
}, {
  name: 'Olivia',
  location: 'Chicago',
  time: '5 minutes ago',
  price: '$14.97'
}, {
  name: 'Sophia',
  location: 'Miami',
  time: '30 seconds ago',
  price: '$14.97'
}, {
  name: 'Ava',
  location: 'New York',
  time: '1 minute ago',
  price: '$14.97'
}, {
  name: 'Isabella',
  location: 'Los Angeles',
  time: '3 minutes ago',
  price: '$14.97'
}, {
  name: 'Mia',
  location: 'Seattle',
  time: '4 minutes ago',
  price: '$14.97'
}, {
  name: 'Amelia',
  location: 'Denver',
  time: '2 minutes ago',
  price: '$14.97'
}, {
  name: 'Harper',
  location: 'Atlanta',
  time: '1 minute ago',
  price: '$14.97'
}, {
  name: 'Evelyn',
  location: 'Boston',
  time: '30 seconds ago',
  price: '$14.97'
}, {
  name: 'Abigail',
  location: 'Phoenix',
  time: '5 minutes ago',
  price: '$14.97'
}, {
  name: 'Ella',
  location: 'Dallas',
  time: '2 minutes ago',
  price: '$14.97'
}, {
  name: 'Scarlett',
  location: 'Houston',
  time: '3 minutes ago',
  price: '$14.97'
}, {
  name: 'Grace',
  location: 'Orlando',
  time: '1 minute ago',
  price: '$14.97'
}, {
  name: 'Chloe',
  location: 'San Diego',
  time: '4 minutes ago',
  price: '$14.97'
}, {
  name: 'Lily',
  location: 'San Francisco',
  time: '2 minutes ago',
  price: '$14.97'
}, {
  name: 'Zoey',
  location: 'Portland',
  time: '30 seconds ago',
  price: '$14.97'
}, {
  name: 'Aria',
  location: 'Minneapolis',
  time: '1 minute ago',
  price: '$14.97'
}, {
  name: 'Nora',
  location: 'Charlotte',
  time: '3 minutes ago',
  price: '$14.97'
}, {
  name: 'Hazel',
  location: 'Tampa',
  time: '2 minutes ago',
  price: '$14.97'
}, {
  name: 'Luna',
  location: 'Nashville',
  time: '4 minutes ago',
  price: '$14.99'
}];
export function RecentPurchaseNotification() {
  const [visible, setVisible] = useState(false);
  const [lastPurchaseIndex, setLastPurchaseIndex] = useState(-1);
  const [currentPurchase, setCurrentPurchase] = useState({
    name: '',
    location: '',
    time: '',
    price: ''
  });
  // Use refs to track timeouts and initialization status
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const nextNotificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);
  // Function to show a random purchase
  const showRandomPurchase = () => {
    // Generate a random index that's different from the last one
    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * purchases.length);
    } while (randomIndex === lastPurchaseIndex && purchases.length > 1);
    // Set current purchase based on random index
    setCurrentPurchase(purchases[randomIndex]);
    setLastPurchaseIndex(randomIndex);
  };
  // Function to handle the show/hide cycle
  const handleNotificationCycle = () => {
    // Show notification
    setVisible(true);
    // Clear any existing timeouts
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (nextNotificationTimeoutRef.current) clearTimeout(nextNotificationTimeoutRef.current);
    // Schedule hide after display duration (3 seconds)
    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false);
      // Schedule next notification after transition completes
      nextNotificationTimeoutRef.current = setTimeout(() => {
        showRandomPurchase();
        handleNotificationCycle();
      }, 2000); // Wait 2 seconds after hiding before showing next (total cycle: 5 seconds)
    }, 3000); // Show for 3 seconds
  };
  useEffect(() => {
    // Only initialize once
    if (isInitializedRef.current) return;
    // Defer notification system initialization to not block initial page load
    const initNotificationSystem = () => {
      if (isInitializedRef.current) return;
      isInitializedRef.current = true;
      showRandomPurchase();
      handleNotificationCycle();
    };
    // Use requestIdleCallback to run this when the browser is idle
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        initialTimeoutRef.current = setTimeout(initNotificationSystem, 3000);
      });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      initialTimeoutRef.current = setTimeout(initNotificationSystem, 3000);
    }
    // Clean up on component unmount
    return () => {
      if (initialTimeoutRef.current) clearTimeout(initialTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      if (nextNotificationTimeoutRef.current) clearTimeout(nextNotificationTimeoutRef.current);
      isInitializedRef.current = false;
    };
  }, []); // Empty dependency array - only run once on mount
  const handleClose = () => {
    setVisible(false);
    // When manually closed, clear current timeouts and schedule next notification
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (nextNotificationTimeoutRef.current) clearTimeout(nextNotificationTimeoutRef.current);
    nextNotificationTimeoutRef.current = setTimeout(() => {
      showRandomPurchase();
      handleNotificationCycle();
    }, 2000);
  };
  return <div className={`fixed md:fixed md:left-1/2 md:transform md:-translate-x-1/2 bottom-4 right-4 md:right-auto max-w-xs w-full bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 transition-all duration-500 ${visible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-10 pointer-events-none'}`} data-id="element-413">
      <div className="p-2" data-id="element-414">
        <div className="flex items-start" data-id="element-415">
          {/* App Icon */}
          <div className="flex-shrink-0 bg-blue-100 rounded-full p-1" data-id="element-416">
            <CheckCircleIcon className="h-6 w-6 text-blue-600" data-id="element-417" />
          </div>
          {/* Header */}
          <div className="ml-1  flex-1" data-id="element-418">
            <p className="text-xs font-medium text-gray-900" data-id="element-419">
              Baby Food Made Simple {currentPurchase.time}
            </p>
            {/* Content */}
            <div className="mt-1 " data-id="element-420">
              <p className="text-xs text-gray-500" data-id="element-421">New Order Completed</p>
              <p className="text-xs font-medium text-gray-900 mt-1  break-words" data-id="element-422">
                {currentPurchase.name} from {currentPurchase.location} purchased
                for {currentPurchase.price}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-500" data-id="element-423">
            <XIcon className="h-3 w-3" data-id="element-424" />
          </button>
        </div>
      </div>
    </div>;
}